import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

interface SearchResult {
  id: string;
  name: string;
  domain: string;
  layer: string;
  score: number;
}

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onResults: (skillIds: string[]) => void;
}

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onClose, onResults }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  const handleSearch = async () => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      // Search using Weaviate's near_text semantic search
      const response = await fetch('http://localhost:8081/v1/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `{
            Get {
              CHISGElement(
                nearText: { concepts: ["${query}"] }
                limit: 20
              ) {
                name
                domain
                layer
                description
                _additional { id distance }
              }
            }
          }`
        }),
      });

      const data = await response.json();
      const elements = data?.data?.Get?.CHISGElement || [];
      
      setResults(elements.map((el: any) => ({
        id: el._additional.id,
        name: el.name,
        domain: el.domain,
        layer: el.layer,
        score: 1 - (el._additional.distance || 0),
      })));
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleSkillSelection = (skillId: string) => {
    setSelectedSkills(prev => 
      prev.includes(skillId) 
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleApplyFilter = () => {
    onResults(selectedSkills);
    handleClose();
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setSelectedSkills([]);
    onClose();
  };

  const getDomainColor = (domain: string): string => {
    const colors: Record<string, string> = {
      'COGNITION': '#4CAF50',
      'PROBLEM SOLVING': '#2196F3',
      'PEOPLE': '#E91E63',
      'SELF': '#9C27B0',
      'COMMUNICATION': '#FF9800',
      'LANGUAGE': '#00BCD4',
      'LITERACY': '#795548',
      'STRATEGIC': '#607D8B',
      'FOCUS & TOOLS': '#3F51B5',
      'NUMBERS': '#F44336',
      'REASONING': '#009688',
    };
    return colors[domain] || '#666';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2D2D3A',
          color: '#fff',
          minHeight: '60vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Search Skills</Typography>
        <IconButton onClick={handleClose} sx={{ color: '#888' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search for skills by name or description..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setQuery('')} size="small">
                    <ClearIcon sx={{ color: '#888' }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                backgroundColor: '#1D1D29',
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#444',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#666',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#009688',
                },
              },
            }}
            sx={{ mb: 2 }}
          />
          
          <Button 
            variant="contained" 
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            sx={{ 
              backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
          </Button>
        </Box>

        {/* Selected Skills */}
        {selectedSkills.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Selected ({selectedSkills.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {results
                .filter(r => selectedSkills.includes(r.id))
                .map(skill => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    size="small"
                    onDelete={() => toggleSkillSelection(skill.id)}
                    sx={{
                      backgroundColor: getDomainColor(skill.domain),
                      color: '#fff',
                    }}
                  />
                ))}
            </Box>
          </Box>
        )}

        {/* Results */}
        <List sx={{ maxHeight: '40vh', overflow: 'auto' }}>
          {results.map((result) => (
            <ListItem key={result.id} disablePadding>
              <ListItemButton
                selected={selectedSkills.includes(result.id)}
                onClick={() => toggleSkillSelection(result.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 150, 136, 0.2)',
                  },
                }}
              >
                <ListItemText
                  primary={result.name}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip 
                        label={result.domain} 
                        size="small" 
                        sx={{ 
                          backgroundColor: getDomainColor(result.domain),
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: 20,
                        }} 
                      />
                      <Chip 
                        label={result.layer} 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          borderColor: '#666',
                          color: '#888',
                          fontSize: '0.65rem',
                          height: 20,
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {Math.round(result.score * 100)}% match
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{ sx: { color: '#fff' } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {results.length === 0 && !loading && query && (
          <Typography sx={{ color: '#666', textAlign: 'center', py: 4 }}>
            No results found. Try a different search term.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #444' }}>
        <Button onClick={handleClose} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          disabled={selectedSkills.length === 0}
          sx={{
            backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
          }}
        >
          Filter Map ({selectedSkills.length} skills)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchDialog;
